import { TimeOption, buildTimeOptions } from '@utils';
import { Col, Select, Typography } from 'antd';
import React from 'react';

const { Text } = Typography;

export interface TimeRangeValue {
  startTime?: string;
  endTime?: string;
}

const fullDayOptions = buildTimeOptions(15);

interface IProps {
  value?: TimeRangeValue;
  options?: TimeOption[];
  disabled?: boolean;
  placeholder?: [string, string];
  onChange?: (value: TimeRangeValue) => void;
}

/**
 * Two selects for picking a start and an end time of the same day.
 * Times outside `options` cannot be picked, and the end time is always after the start time.
 */
export const TimeRangeSelect: React.FC<IProps> = ({
  value,
  options = fullDayOptions,
  disabled,
  placeholder = ['Start time', 'End time'],
  onChange,
}) => {
  const { startTime, endTime } = value ?? {};

  // the last option can only ever be an end time
  const startOptions = options.slice(0, -1);
  const endOptions = options.filter((option) => !startTime || option.value > startTime);

  const handleStartChange = (nextStartTime: string) => {
    const keepsOrder = endTime && endTime > nextStartTime;
    onChange?.({
      startTime: nextStartTime,
      endTime: keepsOrder ? endTime : options.find((option) => option.value > nextStartTime)?.value,
    });
  };

  const handleEndChange = (nextEndTime: string) => {
    onChange?.({ startTime, endTime: nextEndTime });
  };

  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
      <Col flex="auto" className="time-range-select">
        <Select
          style={{ width: '100%', minWidth: '100px' }}
          options={startOptions}
          value={startTime ?? null}
          placeholder={placeholder[0]}
          disabled={disabled}
          onChange={handleStartChange}
        />
      </Col>
      <Text>to</Text>
      <Col flex="auto" className="time-range-select">
        <Select
          style={{ width: '100%', minWidth: '100px' }}
          options={endOptions}
          value={endTime ?? null}
          placeholder={placeholder[1]}
          disabled={disabled || !startTime}
          onChange={handleEndChange}
        />
      </Col>
    </div>
  );
};
